import React from 'react';

import { reverse } from 'named-urls';

import { IconButton, MenuItem } from '@mui/material';

import MoreVertIcon from '@mui/icons-material/MoreVert';

import { useNavigate } from 'react-router-dom';

import { observer } from 'mobx-react';

import MainHeader from 'components/MainHeader';

import { useStore } from 'utils/hooks/store';

import RemoveEntityModal from 'components/RemoveEntityModal';

import { getEntityTranslation } from 'stores/EntityDetails/utils';

import { EntityModules } from 'core/constants';

import { colors } from 'theme/colors';

import { canDeleteFromRecord } from '../deletePermissions';

import { EntityDetailsHeaderProps } from './types';
import { DetailsHeaderWrapper, StyledPopover } from './styles';

// Modules that offer Delete from the record header. The rest have had no trigger since the icon
// button that opened this popover was removed, so they are not opting out.
const DELETE_FROM_RECORD_HEADER = new Set([EntityModules.Risks, EntityModules.Controls, EntityModules.Hazards]);

const EntityDetailsHeader: React.FC<EntityDetailsHeaderProps> = ({
  store,
  title,
  columns,
  breadcrumbs,
  onDeleteFailure,
  menuItems,
  menuTestId,
  canDelete = true,
  wrapColumns = false,
}) => {
  if (!store.details) {
    return null;
  }
  const type = store.entityType;

  const { singular, plural, singularKey } = getEntityTranslation[type];

  const { uuid, entity } = store.details;

  const navigate = useNavigate();
  const { notification, session } = useStore();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const [openDeleteModal, setOpenDeleteModal] = React.useState<boolean>(false);

  const onDelete = async (): Promise<void> => {
    const deleted = await store.delete(uuid);
    if (deleted) {
      navigate(reverse(store.path));
      notification.enqueueSuccessSnackbar(`${singular} deleted successfully`);
      setOpenDeleteModal(false);
    } else {
      if (onDeleteFailure?.()) {
        setOpenDeleteModal(false);
        return;
      }
      notification.enqueueErrorSnackbar(`The ${singular} could not be deleted. Something went wrong.`);
    }
  };

  const openModal = (): void => {
    setAnchorEl(null);
    setOpenDeleteModal(true);
  };

  const closeModal = (): void => {
    setOpenDeleteModal(false);
  };

  const showDeleteAction = DELETE_FROM_RECORD_HEADER.has(type) && canDeleteFromRecord(type, session?.user) && canDelete;

  if (!entity) {
    return <></>;
  }

  return (
    <>
      <MainHeader
        title={title}
        readonlyBreadcrumbs={type === EntityModules.Documents}
        breadcrumbs={
          breadcrumbs || [
            {
              label: plural,
              link: store.path,
            },
            {
              label: 'Details',
              link: store.detailsPath(uuid),
            },
          ]
        }
        action={
          <>
            {!session?.user?.readOnly && (showDeleteAction || menuItems) && (
              <>
                <IconButton
                  data-testid={menuTestId ?? `${type}-details-header-more-options`}
                  onClick={(event) => setAnchorEl(event.currentTarget)}
                >
                  <MoreVertIcon htmlColor={colors.text.secondary} />
                </IconButton>

                <StyledPopover
                  data-testid={`${type}-details-header-popover`}
                  id={`popover-${type}-details`}
                  open={Boolean(anchorEl)}
                  anchorEl={anchorEl}
                  onClose={() => setAnchorEl(null)}
                  anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                  }}
                >
                  {menuItems?.(() => setAnchorEl(null))}
                  {showDeleteAction && (
                    <MenuItem id="remove-button" data-testid={`${singularKey}-delete`} onClick={openModal}>
                      Delete
                    </MenuItem>
                  )}
                </StyledPopover>

                {openDeleteModal && (
                  <RemoveEntityModal onClose={closeModal} handleRemoveEntity={onDelete} entityType={singular} />
                )}
              </>
            )}
          </>
        }
      />
      {columns && (
        <DetailsHeaderWrapper $wrap={wrapColumns}>
          {columns.map((column, index) => (
            <div key={index} id={`${type}-details-header-${column.name.toLowerCase()}`} data-testid={column.testId}>
              <h1>{column.name}</h1>
              {column.value}
            </div>
          ))}
        </DetailsHeaderWrapper>
      )}
    </>
  );
};

export default observer(EntityDetailsHeader);
